import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, Clock } from "lucide-react";
import { Logo } from "@/components/common/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const MAX_ATTEMPTS = 6;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
  const polled = useRef(false);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("failed"); return; }
    if (polled.current) return;
    polled.current = true;

    let attempts = 0;
    const poll = async () => {
      if (attempts >= MAX_ATTEMPTS) { setStatus("timeout"); return; }
      attempts += 1;
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          await refreshUser();
          setStatus("paid");
          return;
        }
        if (res.data.status === "expired") { setStatus("failed"); return; }
      } catch (e) {
        console.warn(`Checkout status poll attempt ${attempts} failed, retrying...`, e);
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [params, refreshUser]);

  const ICONS = {
    checking: <Loader2 className="h-12 w-12 animate-spin text-primary" />,
    paid: <CheckCircle2 className="h-12 w-12 text-[hsl(var(--risk-low))]" />,
    failed: <XCircle className="h-12 w-12 text-[hsl(var(--risk-high))]" />,
    timeout: <Clock className="h-12 w-12 text-[hsl(var(--risk-medium))]" />,
  };
  const TEXT = {
    checking: { t: "Confirming your payment...", d: "This will only take a moment." },
    paid: { t: "You're upgraded!", d: "Your plan is now active. Enjoy unlimited assessments and AI summaries." },
    failed: { t: "Payment not completed", d: "Your payment could not be confirmed. You can try again from the pricing page." },
    timeout: { t: "Still processing", d: "Your payment is taking longer than expected. Check your dashboard shortly." },
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-2xl border bg-card p-8 text-center shadow-[0_10px_30px_rgba(15,23,42,0.08)]">
        <div className="mb-6 flex justify-center"><Logo /></div>
        <div className="flex justify-center">{ICONS[status]}</div>
        <h1 className="mt-5 font-display text-xl font-semibold" data-testid="checkout-status-title">{TEXT[status].t}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{TEXT[status].d}</p>
        <div className="mt-7 flex flex-col gap-2">
          <Button onClick={() => navigate("/app/dashboard")} data-testid="checkout-dashboard-button">Go to dashboard</Button>
          {(status === "failed" || status === "timeout") && (
            <Link to="/app/pricing"><Button variant="outline" className="w-full">Back to pricing</Button></Link>
          )}
        </div>
      </motion.div>
    </div>
  );
}
